// Formatting shared across every screen. Kept in one place so a dollar figure
// looks the same on a kanban card, a table row and a loan file.

/** Full dollars, no cents: $743,000. Loan amounts are never shown to the penny. */
export function usd(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—'
  return `$${Math.round(n).toLocaleString('en-US')}`
}

/** Compact dollars for headline figures: $14.5M, $743k. */
export function usdShort(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—'
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2)}M`
  if (abs >= 1_000) return `$${Math.round(n / 1_000)}k`
  return `$${Math.round(n)}`
}

export function rate(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : `${n.toFixed(3)}%`
}

export function percent(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}%`
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
}

/** Date and clock time — for anything scheduled to the minute, like a drip's next
 *  send, where the day alone leaves out the half of it that matters. */
export function shortDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const today = new Date()
  const sameDay = d.toDateString() === today.toDateString()
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return sameDay ? `Today ${time}` : `${shortDate(iso)}, ${time}`
}

export function relativeDays(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const days = Math.round((Date.now() - d.getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  if (days < 365) return `${Math.round(days / 30)}mo ago`
  return `${Math.round(days / 365)}y ago`
}

/** snake_case → Title Case, for enum values we show verbatim. */
export function titleCase(s: string | null | undefined): string {
  if (!s) return '—'
  return s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?'
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export function addressLine(p: {
  property_street?: string | null
  property_city?: string | null
  property_state?: string | null
}): string {
  return [p.property_street, p.property_city, p.property_state].filter(Boolean).join(', ') || '—'
}

/** Comma-separated role list → array. The API returns roles joined because a
 *  person can hold several across different files. */
export function roleList(roles: string | null | undefined): string[] {
  return (roles ?? '').split(',').map((r) => r.trim()).filter(Boolean)
}

/** A phone number as a person reads it. Numbers are stored in E.164 now
 *  (+13017931709) and were stored as typed before that, so this normalises both
 *  and leaves anything that is not a 10-digit US number exactly as it came. */
export function phoneText(value: string | null | undefined): string {
  const raw = (value ?? '').trim()
  const d = raw.replace(/\D/g, '')
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d
  if (ten.length !== 10) return raw
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
}
